import RoutePageTitle from "components/RoutePageTitle";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Pagination from "@mui/material/Pagination";
import * as XLSX from "xlsx";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useUserContext } from "context/UserContext";
import getGraphQLEndpoint from "utils/getGraphQLEndpoint";
import { getRoleParamForFeature, normalizeRoleLevel } from "utils/roleUtils";
import { ColumnVisibilityMenu, useColumnVisibility } from "./ColumnVisibilityMenu";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const EXPORT_PAGE_SIZE = 1000;
const READABLE_TEXT_COLOR = "#344767";
const ORDER_STATUS_OPTIONS = [
  { value: "completed", label: "已完成" },
  { value: "refunded", label: "已退款" },
  { value: "cancelled", label: "已取消" },
];
const ALL_ORDER_EXPORT_STATUSES = ["completed", "canceled", "paid", "refunded"];

const ORDER_QUERY = `
  query GetOrderQueryV2($input: JSON!) {
    GetOrderQueryV2(input: $input)
  }
`;

const SALES_QUERY = `
  query GetOrderSalesList($input: JSON!) {
    GetOrderSalesList(input: $input)
  }
`;

const REFUND_MUTATION = `
  mutation RefundOrder($input: JSON!) {
    RefundOrder(input: $input)
  }
`;

const UPDATE_NOTE_MUTATION = `
  mutation UpdateOrderNote($input: JSON!) {
    UpdateOrderNote(input: $input)
  }
`;

const ABNORMAL_ORDER_MUTATION = `
  mutation CreateAbnormalOrder($input: JSON!) {
    CreateAbnormalOrder(input: $input)
  }
`;

const orderColumns = [
  { key: "status", label: "訂單狀態" },
  { key: "id", label: "訂單編號" },
  { key: "customColumn2", label: "商品名稱" },
  { key: "orderTime", label: "建立時間" },
  { key: "refunded_at", label: "退款時間" },

  { key: "storeName", label: "店家名稱" },
  { key: "paymentMethod", label: "付款方式" },

  { key: "amount", label: "金額" },
  { key: "redeemPoints", label: "點數" },
  { key: "discount", label: "折扣" },
  { key: "invoiceDate", label: "發票時間" },
  { key: "carrierCode", label: "載具" },

  { key: "invoiceNo", label: "發票號碼" },
  { key: "reference", label: "Reference" },
];

const salesColumns = [
  { key: "order_status", label: "訂單狀態" },
  { key: "order_id", label: "訂單編號" },
  { key: "created_at", label: "建立時間" },
  { key: "product_name", label: "商品名稱" },
  { key: "quantity", label: "數量" },
  { key: "order_amount", label: "訂單金額" },
  { key: "paid_amount", label: "實付金額" },
  { key: "redeem_points", label: "點數" },
  { key: "discount_amount", label: "折扣" },
  { key: "invoice_issued_at", label: "發票時間" },
  { key: "invoice_number", label: "發票號碼" },
  { key: "payment_method", label: "付款方式" },
  { key: "invoice_carrier", label: "發票載具" },
  { key: "is_dispensed", label: "是否有出蛋" },
  { key: "is_compensated", label: "補蛋卷生成" },

  { key: "store_name", label: "商店" },
  { key: "store_code", label: "商店代碼" },
  { key: "payment_id", label: "付款 ID" },
  { key: "sku", label: "SKU" },
  { key: "product_name_full", label: "商品完整名稱" },
  { key: "sales_warehouse_name", label: "銷貨庫別" },
  { key: "warehouse_code", label: "倉庫代碼" },
  { key: "venue_name", label: "場域" },
  { key: "deviceId", label: "機台 ID" },
];

const fieldLabelMap = {
  channel: "下單通道",
  extra_data: "附加資料",
  machine_count: "機台數量",
  order_description: "訂單描述",
  order_type: "訂單類型",
  package_size: "包裝尺寸",
  product_attributes: "商品屬性",
  specification: "規格",
  valid_until: "有效期限",
  work_attributes: "作品屬性",
  type: "訂單類型",
  invoice: "發票資料",
  description: "描述",
  payments: "付款資料",
  reference: "參考編號",
  customColumn1: "操作",
  customColumn2: "商品",
  customColumn3: "更新備註",
  customColumn4: "付款資料",
  customColumn5: "退款時間",
  refunded_at: "退款時間",
  items: "商品明細",
  vas: "加值服務",
  logs: "紀錄",
  payment: "付款資料",
  metadata: "中繼資料",
  order_status: "訂單狀態",
  status: "狀態",
  order_id: "訂單編號",
  id: "訂單編號",
  created_at: "建立時間",
  order_time: "訂單時間",
  orderTime: "訂單時間",
  sku: "料號",
  product_name: "商品名稱",
  product_name_full: "商品完整名稱",
  quantity: "數量",
  order_amount: "訂單金額",
  amount: "金額",
  paid_amount: "實付金額",
  paidAmount: "實付金額",
  redeem_points: "折抵點數",
  redeemPoints: "折抵點數",
  discount_amount: "折扣金額",
  discount: "折扣",
  coupon: "優惠券",
  invoice_issued_at: "發票開立時間",
  invoiceDate: "發票時間",
  invoice_number: "發票號碼",
  invoiceNo: "發票號碼",
  payment_method: "付款方式",
  paymentMethod: "付款方式",
  invoice_carrier: "發票載具",
  carrierCode: "載具",
  is_dispensed: "是否已出貨",
  note: "備註",
  memo: "備忘錄",
  gacha_deviceId: "轉蛋機機台編號",
  deviceId: "機台編號",
  warehouse_code: "倉庫代碼",
  sales_warehouse_name: "銷貨庫別",
  brand: "品牌",
  movie_or_portrait: "IP/角色",
  purchase_category: "採購類別",
  vendor_name: "廠商名稱",
  vendor_code: "廠商代碼",
  nature: "商品性質",
  product_series: "商品系列",
  product_brand: "商品品牌",
  capsule_size: "扭蛋尺寸",
  tags: "標籤",
  store_name: "商店名稱",
  storeName: "商店名稱",
  venue_name: "場域名稱",
  venue_distribution: "場域分區",
  venue_tags: "場域標籤",
  venue_type: "場域類型",
  flow_level: "人流等級",
  machine_location: "機台位置",
  landmark_nearby: "附近地標",
  machine_type: "機台類型",
  target_customers: "目標客群",
  company_tax_id: "統一編號",
  love_code: "愛心碼",
  invoice_email: "發票收件信箱",
  carrier_type: "載具類型",
  payment_id: "付款編號",
  user_phone: "使用者電話",
  phone: "電話",
  user_email: "使用者電子信箱",
  email: "電子信箱",
  is_compensated: "是否已補償",
  compensated_voucher: "補償優惠券",
  store_id: "商店編號",
  store_code: "商店代碼",
  ref: "外部貨號",
};

const fieldLabelOrder = Object.keys(fieldLabelMap).reduce((order, key, index) => {
  order[key] = index;
  return order;
}, {});

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "object") return [];
  const candidates = [
    value.data?.data,
    value.data?.orders,
    value.body?.data,
    value.data?.body?.data,
    value.orders,
    value.data,
    value.body,
    value.items,
  ];
  return candidates.find(Array.isArray) || [];
};

const formatCell = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? "是" : "否";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const normalizeDate = (value) => {
  if (!value) return "";
  return String(value).slice(0, 10);
};

const getTotal = (value, fallback) =>
  Number(
    value?.data?.pagination?.total ||
      value?.body?.pagination?.total ||
      value?.pagination?.total ||
      value?.data?.total ||
      value?.body?.total ||
      value?.total ||
      fallback ||
      0
  );

const getDownloadColumns = (rows, preferredColumns = []) => {
  const keys = Array.from(
    (Array.isArray(rows) ? rows : []).reduce((set, row) => {
      Object.keys(row || {}).forEach((key) => set.add(key));
      return set;
    }, new Set())
  );
  const keySet = new Set(keys);
  const orderedColumns = (Array.isArray(preferredColumns) ? preferredColumns : [])
    .filter((column) => column?.key && keySet.has(column.key))
    .map((column) => ({
      key: column.key,
      label: column.label || fieldLabelMap[column.key] || column.key,
    }));
  const orderedKeys = new Set(orderedColumns.map((column) => column.key));
  const remainingColumns = keys
    .map((key, index) => ({ key, index }))
    .filter(({ key }) => !orderedKeys.has(key))
    .sort((a, b) => {
      const aOrder = fieldLabelOrder[a.key] ?? Number.MAX_SAFE_INTEGER;
      const bOrder = fieldLabelOrder[b.key] ?? Number.MAX_SAFE_INTEGER;
      return aOrder - bOrder || a.index - b.index;
    })
    .map(({ key }) => ({ key, label: fieldLabelMap[key] || key }));

  return [...orderedColumns, ...remainingColumns];
};

const downloadRows = (rows, filename, sheetName, preferredColumns) => {
  const columns = getDownloadColumns(rows, preferredColumns);
  const headers = columns.map((column) => column.label);
  const body = rows.map((row) => columns.map((column) => formatCell(row[column.key])));
  const worksheet = XLSX.utils.aoa_to_sheet([headers, ...body]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  XLSX.writeFile(workbook, filename);
};

const mapOrderRows = (rows) =>
  (Array.isArray(rows) ? rows : []).map((row) => ({
    ...row,
    customColumn2:
      Array.isArray(row?.items) && row.items.length > 0
        ? row.items
            .map((item) => item?.name || "")
            .filter(Boolean)
            .join(", ")
        : row?.customColumn2 || "",
    refunded_at: row?.refunded_at || row?.customColumn5 || "",
  }));

function JsonTable({ value, labels = fieldLabelMap }) {
  const rows = Array.isArray(value) ? value : value ? [value] : [];
  const keys = Array.from(
    rows.reduce((set, row) => {
      Object.keys(row || {}).forEach((key) => set.add(key));
      return set;
    }, new Set())
  );

  if (!rows.length) {
    return <MDTypography variant="button">無資料</MDTypography>;
  }

  return (
    <TableContainer component={Paper} sx={{ maxHeight: 420 }}>
      <Table size="small" stickyHeader>
        <TableHead sx={{ display: "table-header-group" }}>
          <TableRow>
            {keys.map((key) => (
              <TableCell key={key}>{labels[key] || key}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={index}>
              {keys.map((key) => (
                <TableCell key={key}>{formatCell(row?.[key])}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

JsonTable.propTypes = {
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  labels: PropTypes.objectOf(PropTypes.string),
};

function OrderDetailPermissionsPage() {
  const { userData, roleLevel, merchantTags } = useUserContext() || {};
  const backendUrl = useMemo(getGraphQLEndpoint, []);
  const email = userData?.email ? userData.email.trim().toLowerCase() : "";
  const normalizedRole = getRoleParamForFeature("transactions", roleLevel, email);
  const showMerchantFilter = normalizeRoleLevel(roleLevel) === 3;

  const [tab, setTab] = useState("orders");
  const [rows, setRows] = useState([]);
  const [salesRows, setSalesRows] = useState([]);
  const merchantOptions = useMemo(
    () => (Array.isArray(merchantTags) ? merchantTags.filter(Boolean) : []),
    [merchantTags]
  );
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [filters, setFilters] = useState({
    id: "",
    start: "",
    end: "",
    orderStatus: "",
    showDetail: true,
    merchantTags: Array.isArray(merchantTags) ? merchantTags : [],
  });
  const [modal, setModal] = useState({ type: "", row: null });
  const [note, setNote] = useState("");

  const updateFilters = useCallback((updater) => {
    setPage(1);
    setFilters((prev) => (typeof updater === "function" ? updater(prev) : { ...prev, ...updater }));
  }, []);

  const fetchGraphQL = useCallback(
    async (query, variables = {}) => {
      const headers = { "Content-Type": "application/json", Accept: "application/json" };
      const response = await fetch(backendUrl, {
        method: "POST",
        headers,
        body: JSON.stringify({ query, variables }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok || json?.errors?.length) {
        throw new Error(
          json?.errors?.[0]?.message || `GraphQL request failed (${response.status})`
        );
      }
      return json?.data || {};
    },
    [backendUrl]
  );

  const buildInput = useCallback(
    (targetTab, override = {}) => {
      const payload = {
        id: filters.id.trim(),
        start: normalizeDate(filters.start),
        end: normalizeDate(filters.end),
        page: override.page || page,
        size: override.size || pageSize,
        showDetail: filters.showDetail,
        email,
        role: normalizedRole,
      };
      if (targetTab === "orders" && filters.orderStatus) {
        payload.status = [filters.orderStatus];
      }
      if (filters.merchantTags.length) payload.merchantCode = filters.merchantTags;
      return payload;
    },
    [email, filters, normalizedRole, page, pageSize]
  );

  const fetchOrders = useCallback(async () => {
    if (!email) return;
    setLoading(true);
    setError("");
    try {
      const data = await fetchGraphQL(ORDER_QUERY, { input: buildInput("orders") });
      const result = data?.GetOrderQueryV2 || {};
      const nextRows = mapOrderRows(toArray(result)).sort(
        (a, b) =>
          new Date(b.created_at || b.orderTime || b.order_time || 0) -
          new Date(a.created_at || a.orderTime || a.order_time || 0)
      );
      setRows(nextRows);
      setTotal(getTotal(result, nextRows.length));
    } catch (err) {
      setError(err.message || "查詢訂單失敗");
    } finally {
      setLoading(false);
    }
  }, [buildInput, email, fetchGraphQL]);

  const fetchSales = useCallback(async () => {
    if (!email) return;
    setLoading(true);
    setError("");
    try {
      const data = await fetchGraphQL(SALES_QUERY, { input: buildInput("sales") });
      const result = data?.GetOrderSalesList || {};
      const nextRows = toArray(result);
      setSalesRows(nextRows);
      setTotal(getTotal(result, nextRows.length));
    } catch (err) {
      setError(err.message || "查詢銷貨列表失敗");
    } finally {
      setLoading(false);
    }
  }, [buildInput, email, fetchGraphQL]);

  useEffect(() => {
    if (tab === "orders") {
      fetchOrders();
    } else {
      fetchSales();
    }
  }, [fetchOrders, fetchSales, tab]);

  const handleSearch = useCallback(() => {
    setPage(1);
    if (page === 1) {
      if (tab === "orders") {
        fetchOrders();
      } else {
        fetchSales();
      }
    }
  }, [fetchOrders, fetchSales, page, tab]);

  const handleRefund = async (row) => {
    const orderID = row?.id || row?.order_id;
    if (!orderID) return;
    try {
      await fetchGraphQL(REFUND_MUTATION, { input: { orderId: orderID, email } });
      await fetchOrders();
    } catch (err) {
      setError(err.message || "退款/退單失敗");
    }
  };

  const handleSaveNote = async () => {
    const orderID = modal.row?.id || modal.row?.order_id;
    if (!orderID) return;
    try {
      await fetchGraphQL(UPDATE_NOTE_MUTATION, { input: { order_id: orderID, note } });
      setModal({ type: "", row: null });
      setNote("");
      if (tab === "sales") {
        await fetchSales();
      } else {
        await fetchOrders();
      }
    } catch (err) {
      setError(err.message || "更新備註失敗");
    }
  };

  const handleCreateAbnormalOrder = async (row) => {
    const orderID = row?.order_id || row?.id;
    if (!orderID) return;
    try {
      await fetchGraphQL(ABNORMAL_ORDER_MUTATION, { input: { order_id: orderID, email } });
      setModal({ type: "message", row: { message: "補蛋券已送出", order_id: orderID } });
    } catch (err) {
      setError(err.message || "補蛋券建立失敗");
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    setError("");
    try {
      const isOrders = tab === "orders";
      const query = isOrders ? ORDER_QUERY : SALES_QUERY;
      const field = isOrders ? "GetOrderQueryV2" : "GetOrderSalesList";
      const input = buildInput(tab, {
        page: 1,
        size: EXPORT_PAGE_SIZE,
      });

      // The normal all-status view keeps refunded orders hidden. Exports must
      // include every finance-relevant status without changing that view.
      if (isOrders && !filters.orderStatus) {
        input.status = ALL_ORDER_EXPORT_STATUSES;
      }
      const data = await fetchGraphQL(query, { input });
      const result = data?.[field] || {};
      const firstPageRows = isOrders ? mapOrderRows(toArray(result)) : toArray(result);
      const rows = [...firstPageRows];

      const totalPages = Math.ceil(getTotal(result, rows.length) / EXPORT_PAGE_SIZE);
      for (let currentPage = 2; currentPage <= totalPages; currentPage += 1) {
        const pageData = await fetchGraphQL(query, {
          input: { ...input, page: currentPage },
        });
        const pageRows = toArray(pageData?.[field] || {});
        rows.push(...(isOrders ? mapOrderRows(pageRows) : pageRows));
      }

      downloadRows(
        rows,
        isOrders ? "orders.xlsx" : "sales-list.xlsx",
        isOrders ? "Orders" : "Sales",
        isOrders ? orderColumns : salesColumns
      );
    } catch (err) {
      setError(err.message || "下載失敗");
    } finally {
      setDownloading(false);
    }
  };

  const displayedRows = tab === "orders" ? rows : salesRows;
  const displayedColumns = tab === "orders" ? orderColumns : salesColumns;
  const { columnHeadersSetting, handleChangeColumnHeaderVisible, visibleColumns } =
    useColumnVisibility(displayedColumns, tab);
  const displayedTotal = total || displayedRows.length;
  const totalPages = Math.max(1, Math.ceil(displayedTotal / pageSize));
  const displayedPage = Math.min(page, totalPages);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={2}
              >
                <MDTypography variant="h6" color="white">
                  <RoutePageTitle />
                </MDTypography>
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={handleDownload}
                  disabled={downloading}
                >
                  {downloading ? "下載中..." : "下載"}
                </Button>
              </MDBox>
              <MDBox p={3}>
                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}
                <Tabs
                  value={tab}
                  onChange={(_, value) => {
                    setTab(value);
                    setPage(1);
                  }}
                  sx={{ mb: 3 }}
                >
                  <Tab label="查詢訂單" value="orders" />
                  <Tab label="銷貨列表" value="sales" />
                </Tabs>
                <Grid container spacing={2} alignItems="center" mb={3}>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      label="訂單 ID / 綠界單號"
                      placeholder="可輸入完整訂單 ID 或 20 碼綠界單號"
                      value={filters.id}
                      onChange={(e) => updateFilters({ id: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      type="date"
                      label="開始日期"
                      InputLabelProps={{ shrink: true }}
                      value={filters.start}
                      onChange={(e) => updateFilters({ start: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      type="date"
                      label="結束日期"
                      InputLabelProps={{ shrink: true }}
                      value={filters.end}
                      onChange={(e) => updateFilters({ end: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    {tab === "orders" && (
                      <TextField
                        select
                        fullWidth
                        label="訂單狀態"
                        value={filters.orderStatus}
                        onChange={(e) => updateFilters({ orderStatus: e.target.value })}
                      >
                        <MenuItem value="">全部</MenuItem>
                        {ORDER_STATUS_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={filters.showDetail}
                          onChange={(e) => updateFilters({ showDetail: e.target.checked })}
                        />
                      }
                      label="顯示明細"
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Button
                      fullWidth
                      variant="contained"
                      sx={{ color: "#fff" }}
                      onClick={handleSearch}
                    >
                      查詢
                    </Button>
                  </Grid>
                  {showMerchantFilter && (
                    <Grid item xs={12}>
                      <TextField
                        select
                        fullWidth
                        SelectProps={{
                          multiple: true,
                          renderValue: (selected) => (
                            <Stack direction="row" gap={1} flexWrap="wrap">
                              {selected.map((value) => (
                                <Chip key={value} label={value} size="small" />
                              ))}
                            </Stack>
                          ),
                        }}
                        label="商戶標籤"
                        value={filters.merchantTags}
                        onChange={(e) => updateFilters({ merchantTags: e.target.value })}
                      >
                        {merchantOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  )}
                </Grid>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  mb={2}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <TextField
                    select
                    size="small"
                    label="每頁筆數"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                    sx={{ width: 140 }}
                  >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <MenuItem key={size} value={size}>
                        {size}
                      </MenuItem>
                    ))}
                  </TextField>
                  <MDTypography variant="button" sx={{ color: READABLE_TEXT_COLOR }}>
                    共 {displayedTotal} 筆，第 {displayedPage} / {totalPages} 頁
                  </MDTypography>
                  <MDBox ml="auto">
                    <ColumnVisibilityMenu
                      columnHeadersSetting={columnHeadersSetting}
                      onChangeColumnHeaderVisible={handleChangeColumnHeaderVisible}
                    />
                  </MDBox>
                </Stack>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" py={6}>
                    <CircularProgress />
                  </MDBox>
                ) : (
                  <TableContainer component={Paper} sx={{ maxHeight: 650 }}>
                    <Table stickyHeader>
                      <TableHead sx={{ display: "table-header-group", whiteSpace: "nowrap" }}>
                        <TableRow>
                          {visibleColumns.map((column) => (
                            <TableCell key={column.key} sx={{ textAlign: "center" }}>
                              {column.label}
                            </TableCell>
                          ))}
                          <TableCell sx={{ textAlign: "center" }}>操作</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody sx={{ whiteSpace: "nowrap" }}>
                        {displayedRows.map((row, index) => (
                          <TableRow key={row.id || row.order_id || index}>
                            {visibleColumns.map((column) => (
                              <TableCell key={column.key} sx={{ textAlign: "center" }}>
                                {formatCell(row[column.key])}
                              </TableCell>
                            ))}
                            <TableCell>
                              <Stack direction="row" spacing={1}>
                                {tab === "orders" ? (
                                  <>
                                    <Button
                                      size="small"
                                      color="error"
                                      onClick={() => handleRefund(row)}
                                    >
                                      退單
                                    </Button>
                                    <Button
                                      size="small"
                                      onClick={() => setModal({ type: "details", row })}
                                    >
                                      明細
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    <Button
                                      size="small"
                                      onClick={() => setModal({ type: "details", row })}
                                    >
                                      明細
                                    </Button>
                                    <Button
                                      size="small"
                                      onClick={() => handleCreateAbnormalOrder(row)}
                                    >
                                      補蛋券
                                    </Button>
                                    <Button
                                      size="small"
                                      onClick={() => {
                                        setNote(row.note || "");
                                        setModal({ type: "note", row });
                                      }}
                                    >
                                      備註
                                    </Button>
                                  </>
                                )}
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
                <MDBox mt={2} display="flex" justifyContent="flex-end">
                  <Pagination
                    count={totalPages}
                    page={displayedPage}
                    color="primary"
                    onChange={(_, value) => setPage(value)}
                  />
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog
        open={modal.type === "details"}
        onClose={() => setModal({ type: "", row: null })}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>交易明細</DialogTitle>
        <DialogContent>
          <Tabs value="metadata" sx={{ mb: 2 }}>
            <Tab label="資料" value="metadata" />
          </Tabs>
          <JsonTable value={modal.row} />
          <MDBox mt={3}>
            <MDTypography variant="h6">Items</MDTypography>
            <JsonTable value={modal.row?.items} />
          </MDBox>
          <MDBox mt={3}>
            <MDTypography variant="h6">VAS</MDTypography>
            <JsonTable value={modal.row?.vas} />
          </MDBox>
          <MDBox mt={3}>
            <MDTypography variant="h6">Logs</MDTypography>
            <JsonTable value={modal.row?.logs} />
          </MDBox>
          <MDBox mt={3}>
            <MDTypography variant="h6">Payment</MDTypography>
            <JsonTable value={modal.row?.payment} />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModal({ type: "", row: null })}>關閉</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={modal.type === "note"} onClose={() => setModal({ type: "", row: null })}>
        <DialogTitle>更新備註</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            margin="normal"
            label="備註"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModal({ type: "", row: null })}>取消</Button>
          <Button variant="contained" sx={{ color: "#fff" }} onClick={handleSaveNote}>
            儲存
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={modal.type === "message"} onClose={() => setModal({ type: "", row: null })}>
        <DialogTitle>執行結果</DialogTitle>
        <DialogContent>
          <JsonTable value={modal.row} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModal({ type: "", row: null })}>關閉</Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default OrderDetailPermissionsPage;
